import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMXN } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Heart, ShoppingCart } from "lucide-react";

import SafeImage from "@/components/SafeImage";

export default function WishlistPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    api.get("/wishlist").then((r) => {
      setItems(r.data);
      setLoading(false);
    });
  }, [user, authLoading]);

  const toggleItem = async (product) => {
    await api.post("/wishlist/toggle", { product_id: product.id });
    setItems((prev) => prev.filter((p) => p.id !== product.id));
  };

  if (authLoading || (user && loading)) {
    return <div className="min-h-screen bg-arre-black flex items-center justify-center"><div className="text-white/50">Cargando...</div></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-arre-black flex items-center justify-center py-20 px-6">
        <div className="text-center max-w-md">
          <Heart className="w-16 h-16 text-arre-red mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-display text-4xl text-white tracking-wider uppercase leading-none mb-3">Tus favoritos</h1>
          <p className="text-white/60 mb-8">Inicia sesión para guardar cortes y paquetes en tu lista de deseos.</p>
          <Link to="/login?redirect=/favoritos" className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-lg tracking-widest uppercase px-6 py-3" data-testid="wishlist-login-cta">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-arre-black min-h-screen py-16" data-testid="wishlist-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="text-xs tracking-[0.35em] uppercase text-arre-red mb-3">⟡ Favoritos</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-wider uppercase text-white leading-none mb-10">
          Tu <span className="text-arre-red italic font-serif-display normal-case tracking-tight">lista</span>
        </h1>

        {items.length === 0 ? (
          <div className="bg-arre-surface border border-arre-border p-10 text-center">
            <Heart className="w-14 h-14 text-white/20 mx-auto mb-4" strokeWidth={1} />
            <p className="font-display text-2xl tracking-wider uppercase text-white mb-2">Vacío por ahora</p>
            <p className="text-white/60 text-sm mb-6">Marca con ❤️ los cortes que quieres recordar.</p>
            <Link to="/catalogo/cortes" className="btn-arre bg-arre-red text-white font-display text-lg tracking-widest uppercase px-6 py-3 inline-block">Explorar cortes</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((p) => {
              const v = p.variants?.[0];
              const price = v?.price ?? p.price;
              return (
                <div key={p.id} className="group bg-arre-surface border border-arre-border hover:border-arre-red transition-colors" data-testid={`wishlist-item-${p.slug}`}>
                  <Link to={`/producto/${p.slug}`} className="block aspect-[4/5] overflow-hidden bg-arre-black hover-zoom">
                    <SafeImage src={p.image} alt={p.name} fallbackLabel={p.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100" />
                  </Link>
                  <div className="p-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-1">{v?.label?.split(" ")[0] || p.unit}</div>
                    <Link to={`/producto/${p.slug}`} className="font-display text-xl tracking-wider uppercase text-white leading-none hover:text-arre-red">{p.name}</Link>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-display text-lg text-white">{fmtMXN(price)}</span>
                      <button onClick={() => toggleItem(p)} className="text-arre-red hover:text-white transition" data-testid={`unfav-${p.slug}`}>
                        <Heart className="w-5 h-5" fill="currentColor" />
                      </button>
                    </div>
                    {!p.variants || p.variants.length <= 1 ? (
                      <button
                        onClick={() => addItem({ type: "product", id: p.id, variant_id: v?.id || null, name: p.name, price, image: p.image, unit: v?.label || p.unit })}
                        className="mt-3 w-full btn-arre bg-arre-red/10 hover:bg-arre-red text-arre-red hover:text-white font-display text-xs tracking-widest uppercase py-2 inline-flex items-center justify-center gap-1"
                        data-testid={`add-from-wishlist-${p.slug}`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5" /> Agregar
                      </button>
                    ) : (
                      <Link to={`/producto/${p.slug}`} className="mt-3 block text-center btn-arre bg-arre-red/10 hover:bg-arre-red text-arre-red hover:text-white font-display text-xs tracking-widest uppercase py-2">
                        Elegir →
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
